import { useApolloClient, useQuery } from '@apollo/client';
import { DocumentArrowDownIcon } from '@heroicons/react/20/solid';
import {
  downloadPresignedUrl,
  hsaInvoiceErrorMessage,
  LoadingOrError,
  orderHasHsaInvoiceItems,
  ShowStatus,
  timestampFormatter,
} from '@retainerclub/common';
import { GET_ORDERS } from '@retainerclub/customer-api';
import { CustomerCard } from '@retainerclub/customer-shared';
import { useLocale, useToast } from '@retainerclub/hooks';
import { ACTION_GENERATE_HSA_INVOICE } from '@retainerclub/shared-api';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import OrdersHistoryNoOrders from './OrdersHistoryNoOrders';
import OrdersHistoryOrderItem from './OrdersHistoryOrderItem';

function OrdersHistory({ limit = null }) {
  const client = useApolloClient();
  const { locale } = useLocale();
  const { setToast } = useToast();
  const [orders, setOrders] = useState([]);
  const [generatingInvoiceOrderId, setGeneratingInvoiceOrderId] =
    useState(null);

  const { error, loading } = useQuery(GET_ORDERS, {
    onCompleted: (data) => {
      if (data?.orders?.length > 0) {
        setOrders(
          [...data.orders].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          ),
        );
      }
    },
  });

  const ordersToDisplay = useMemo(() => {
    if (limit) {
      return orders.slice(0, 3);
    }

    return orders;
  }, [limit, orders]);

  const shouldRender = LoadingOrError({
    loading,
    error,
    data: { orders },
    dataKey: 'orders',
    title: 'Orders',
  });

  function scanAvailableBadge(orderItems) {
    return orderItems.every((e) => e.patient.scanReady);
  }

  async function handleGenerateHsaInvoice(order) {
    if (generatingInvoiceOrderId) {
      return;
    }

    setGeneratingInvoiceOrderId(order.id);

    try {
      const { data: result } = await client.mutate({
        mutation: ACTION_GENERATE_HSA_INVOICE,
        variables: {
          input: { id: order.id },
        },
      });

      const response = result?.actionGenerateHsaInvoice;

      if (!(response?.success && response?.url)) {
        setToast({
          show: true,
          title: 'Error',
          message: hsaInvoiceErrorMessage(response),
          type: 'error',
        });
        return;
      }

      downloadPresignedUrl(response.url);
    } catch (error) {
      setToast({
        show: true,
        title: 'Error',
        message:
          error.message || 'Unable to generate HSA/FSA invoice at this time',
        type: 'error',
      });
    } finally {
      setGeneratingInvoiceOrderId(null);
    }
  }

  return ordersToDisplay.length > 0 ? (
    <CustomerCard title={limit ? 'My Recent Orders' : 'Order History'}>
      <div className="relative space-y-16 sm:space-y-24">
        {shouldRender
          ? ordersToDisplay?.map((order) => (
              <div key={order.id}>
                <div className="bg-rcgray-200 px-4 py-6 sm:rounded-lg sm:p-6 md:flex md:items-center md:justify-between md:space-x-4">
                  <dl className="flex-auto space-y-4 divide-y divide-gray-200 text-gray-600 text-sm md:grid md:grid-cols-4 md:gap-x-4 md:space-y-0 md:divide-y-0 lg:flex-none lg:gap-x-4">
                    <div className="flex justify-between md:block">
                      <dt className="font-medium text-gray-900">
                        Order number
                      </dt>
                      <dd className="md:mt-1">{order.hexCode}</dd>
                    </div>
                    <div className="flex justify-between pt-4 md:block md:pt-0">
                      <dt className="font-medium text-gray-900">Date placed</dt>
                      <dd className="md:mt-1">
                        <time dateTime={order.createdAt}>
                          {timestampFormatter(order.createdAt)}
                        </time>
                      </dd>
                    </div>

                    <div className="flex justify-between pt-4 font-medium text-gray-900 md:block md:pt-0">
                      <dt>Status</dt>
                      <dd className="md:mt-1">
                        <ShowStatus
                          state={order.state}
                          scanAvailable={scanAvailableBadge(order.orderItems)}
                        />
                      </dd>
                    </div>
                    <div className="flex justify-between pt-4 font-medium text-gray-900 md:block md:pt-0">
                      <dt>Total amount</dt>
                      <dd className="flex items-center justify-between gap-x-4 md:mt-1 md:gap-x-0">
                        <span>
                          {(order.total / 100).toLocaleString(locale, {
                            style: 'currency',
                            currency: 'USD',
                          })}
                        </span>
                        {order.courtesyOrder && (
                          <ShowStatus state="Courtesy Order" size="small" />
                        )}
                      </dd>
                    </div>
                  </dl>
                  {orderHasHsaInvoiceItems(order) &&
                  order.state !== 'cancelled' ? (
                    <button
                      type="button"
                      onClick={() => handleGenerateHsaInvoice(order)}
                      disabled={generatingInvoiceOrderId === order.id}
                      className="mt-5 inline-flex items-center gap-x-2 rounded-md bg-white px-3 py-2 font-semibold text-gray-900 text-sm shadow-sm ring-1 ring-gray-300 ring-inset hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 md:mt-0"
                    >
                      <DocumentArrowDownIcon className="size-4" />
                      {generatingInvoiceOrderId === order.id
                        ? 'Generating'
                        : 'Purchase Receipt'}
                    </button>
                  ) : null}
                </div>

                <div className="mt-4 flow-root px-4 sm:px-0">
                  <div className="-my-4 divide-y divide-gray-200 sm:-my-8">
                    {order?.orderItems?.map((orderItem) => (
                      <OrdersHistoryOrderItem
                        key={orderItem.id}
                        orderItem={orderItem}
                        productionOrders={order.productionOrders}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))
          : shouldRender}
      </div>
    </CustomerCard>
  ) : (
    <OrdersHistoryNoOrders />
  );
}

OrdersHistory.propTypes = {
  limit: PropTypes.bool,
};

export default OrdersHistory;
